import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { toast } from "sonner";

import { PlateInput } from "../components/PlateInput";
import { VehicleCard } from "../components/VehicleCard";
import { PartSearch } from "../components/PartSearch";
import { SupplierComparator } from "../components/SupplierComparator";
import { AIAdvisor } from "../components/AIAdvisor";
import { HistoryDrawer } from "../components/HistoryDrawer";
import { TrustSection } from "../components/TrustSection";
import { HowItWorks } from "../components/HowItWorks";
import { DemoPlatesHint } from "../components/DemoPlatesHint";
import { SuppliersStrip } from "../components/SuppliersStrip";
import { VehicleTypeSelector, VEHICLE_TYPES } from "../components/VehicleTypeSelector";
import { PlSousTypeSelector, getPlSousTypeLabel } from "../components/PlSousTypeSelector";
import { SEO } from "../components/SEO";

import {
  plateLookup,
  fetchCategories,
  fetchSuppliers,
  searchParts,
  getAiAdvice,
  fetchDemoPlates,
} from "../lib/api";
import { getHistory, addHistoryEntry, clearHistory } from "../lib/history";

const TYPE_LABELS = {
  auto: "Voiture",
  moto: "Moto",
  poids_lourd: "Poids lourd",
};

export default function HomePage({ historyOpen, onCloseHistory }) {
  const [vehicleType, setVehicleType] = useState("auto");
  const [sousTypePl, setSousTypePl] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [plateLoading, setPlateLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [demoPlates, setDemoPlates] = useState([]);

  const [offers, setOffers] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentPartLabel, setCurrentPartLabel] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);

  const [historyItems, setHistoryItems] = useState([]);

  const resultsRef = useRef(null);
  const partSearchRef = useRef(null);

  // Recharge catégories et fournisseurs selon type + sous-type PL
  useEffect(() => {
    const effectiveSousType = vehicleType === "poids_lourd" ? sousTypePl : null;
    fetchCategories(vehicleType, effectiveSousType)
      .then((d) => setCategories(d.categories || []))
      .catch(() => {});
    fetchSuppliers(vehicleType).then((d) => setSuppliers(d.suppliers || [])).catch(() => {});
  }, [vehicleType, sousTypePl]);

  useEffect(() => {
    fetchDemoPlates().then((d) => setDemoPlates(d.plates || [])).catch(() => {});
    setHistoryItems(getHistory());
  }, []);

  const handleTypeChange = (type) => {
    setVehicleType(type);
    // Reset sous-type si on quitte PL
    if (type !== "poids_lourd") {
      setSousTypePl(null);
    }
    // Réinitialise le véhicule si le type change (évite les incohérences)
    if (vehicle && vehicle.type_vehicule !== type) {
      setVehicle(null);
      setOffers([]);
      setAdvice(null);
    }
  };

  const handlePlateSubmit = async (plaque) => {
    setPlateLoading(true);
    setOffers([]);
    setAdvice(null);
    try {
      const data = await plateLookup(plaque);
      const detectedType = data.vehicle.type_vehicule || "auto";
      setVehicle(data.vehicle);

      // Si plaque PL, aligne aussi le sous-type détecté
      if (detectedType === "poids_lourd" && data.vehicle.sous_type_pl) {
        setSousTypePl(data.vehicle.sous_type_pl);
      }

      // Si la plaque identifiée est d'un autre type que celui sélectionné,
      // on ajuste automatiquement en informant l'utilisateur
      if (detectedType !== vehicleType) {
        setVehicleType(detectedType);
        toast.info(
          `On a reconnu un ${TYPE_LABELS[detectedType] || detectedType} — on bascule sur les fournisseurs adaptés`,
        );
      } else {
        toast.success(`${data.vehicle.marque} ${data.vehicle.modele} identifié`, {
          description: `${data.vehicle.annee} · ${data.vehicle.motorisation || data.vehicle.type_carrosserie}`,
        });
      }
      // Recharge liste des fournisseurs selon type véhicule détecté
      fetchSuppliers(detectedType)
        .then((d) => setSuppliers(d.suppliers || []))
        .catch(() => {});
      const effectiveSousType =
        detectedType === "poids_lourd"
          ? data.vehicle.sous_type_pl || sousTypePl
          : null;
      fetchCategories(detectedType, effectiveSousType)
        .then((d) => setCategories(d.categories || []))
        .catch(() => {});
      setTimeout(() => {
        partSearchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'identification.";
      toast.error("Identification impossible", { description: msg });
    } finally {
      setPlateLoading(false);
    }
  };

  const handlePartSearch = async ({ part_query, category_id }) => {
    if (!vehicle) {
      toast.error("Identifiez d'abord votre véhicule.");
      return;
    }
    setSearchLoading(true);
    setAdviceLoading(true);
    setOffers([]);
    setAdvice(null);

    // Lance l'IA en parallèle sans bloquer l'affichage du comparateur
    getAiAdvice({ vehicle, part_query, category_id })
      .then((d) => setAdvice(d?.advice || null))
      .catch(() => setAdvice(null))
      .finally(() => setAdviceLoading(false));

    try {
      const partsData = await searchParts({ vehicle, part_query, category_id });
      setOffers(partsData.offers || []);
      setCurrentCategory(partsData.category);
      setCurrentPartLabel(partsData.part_label);
      // Enregistre dans l'historique local
      const next = addHistoryEntry({
        plaque: vehicle.plaque,
        marque: vehicle.marque,
        modele: vehicle.modele,
        annee: vehicle.annee,
        part_query,
        category_id,
        vehicle,
      });
      setHistoryItems(next);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la recherche.";
      toast.error("Comparateur indisponible", { description: msg });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleReplayHistory = async (item) => {
    onCloseHistory(false);
    if (item.vehicle) {
      setVehicle(item.vehicle);
      if (item.vehicle.type_vehicule) {
        setVehicleType(item.vehicle.type_vehicule);
      }
      await handlePartSearch({
        part_query: item.part_query,
        category_id: item.category_id,
      });
    } else {
      await handlePlateSubmit(item.plaque);
    }
  };

  const handleClearHistory = () => {
    clearHistory();
    setHistoryItems([]);
    toast.success("Historique effacé");
  };

  const heroStats = useMemo(
    () => [
      { k: suppliers.length || 14, v: "Fournisseurs comparés" },
      { k: categories.length || 20, v: "Catégories pièces" },
      { k: "1,2s", v: "Identification IA" },
      { k: "0€", v: "Surcoût pour vous" },
    ],
    [suppliers.length, categories.length],
  );

  return (
    <>
      <SEO
        title="Comparateur pièces auto, moto et poids lourd par plaque"
        description="Trouve la bonne pièce détachée pour ta voiture, ta moto ou ton poids lourd en entrant simplement ta plaque d'immatriculation. Comparateur IA : Oscaro, Autodoc, Mister Auto, Ovoko…"
      />
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-slate-800/70"
      >
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-cyan-500/10 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
            <div
              data-testid="hero-eyebrow"
              className="reveal-1 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 px-4 py-1.5"
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-400" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-300">
                Comparateur IA · Auto · Moto · Poids lourd · France
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="reveal-2 mt-6 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl"
            >
              Marre de chercher pendant des heures la bonne pièce pour ta voiture, ta moto ou ton poids lourd ?
            </h1>

            <p
              data-testid="hero-punch"
              className="reveal-2 mt-5 max-w-3xl font-heading text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl"
            >
              <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-amber-300 bg-clip-text text-transparent">
                Entre ta plaque, on identifie ton véhicule et on compare les pièces chez les meilleurs fournisseurs.
              </span>
            </p>

            <p
              data-testid="hero-subtitle"
              className="reveal-3 mt-5 max-w-2xl text-sm text-slate-400 sm:text-base"
            >
              Choisis ton type de véhicule, saisis ta plaque française : notre IA reconnaît ton véhicule
              en 1,2 seconde et compare instantanément les offres de nos fournisseurs spécialisés
              auto, moto et poids lourds. Zéro erreur de référence.
            </p>

            {/* Sélecteur de type (3 vignettes) */}
            <div className="reveal-3 mt-10 w-full max-w-3xl">
              <VehicleTypeSelector value={vehicleType} onChange={handleTypeChange} />
              {vehicleType === "poids_lourd" && (
                <PlSousTypeSelector value={sousTypePl} onChange={setSousTypePl} />
              )}
            </div>

            {/* Champ plaque */}
            <div className="reveal-4 mt-8 w-full flex flex-col items-center">
              <PlateInput onSubmit={handlePlateSubmit} loading={plateLoading} />
              <DemoPlatesHint
                plates={demoPlates}
                onPick={(p) => handlePlateSubmit(p)}
                selectedType={vehicleType}
              />
            </div>

            <div className="mt-12 grid w-full grid-cols-2 gap-4 sm:grid-cols-4">
              {heroStats.map((s, i) => (
                <div
                  key={i}
                  data-testid={`hero-stat-${i}`}
                  className="surface-card rounded-xl px-4 py-3 text-left"
                >
                  <div className="font-heading text-2xl font-extrabold text-slate-50">
                    {s.k}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle + Search */}
      {vehicle && (
        <section
          ref={partSearchRef}
          data-testid="workflow-section"
          className="mx-auto max-w-7xl px-4 py-12 sm:px-8"
        >
          <div className="space-y-6">
            <VehicleCard vehicle={vehicle} />
            <PartSearch
              categories={categories}
              onSearch={handlePartSearch}
              loading={searchLoading}
            />
          </div>
        </section>
      )}

      {/* Results */}
      {(offers.length > 0 || searchLoading || advice || adviceLoading) && (
        <section
          ref={resultsRef}
          data-testid="results-section"
          className="mx-auto max-w-7xl px-4 pb-12 sm:px-8"
        >
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SupplierComparator
                offers={offers}
                partLabel={currentPartLabel}
                categoryLabel={currentCategory?.label}
                vehicle={vehicle}
                loading={searchLoading}
              />
            </div>
            <div className="lg:col-span-1">
              <AIAdvisor advice={advice} loading={adviceLoading} />
            </div>
          </div>
        </section>
      )}

      <HowItWorks />
      <SuppliersStrip suppliers={suppliers} />
      <TrustSection />

      <HistoryDrawer
        open={historyOpen}
        onOpenChange={onCloseHistory}
        items={historyItems}
        onClear={handleClearHistory}
        onReplay={handleReplayHistory}
      />
    </>
  );
}
